import { useEffect } from 'react';
import { useShepherd } from 'react-shepherd';
import { StepOptions, TourOptions } from 'shepherd.js';
import { useLocation } from 'react-router';
import 'shepherd.js/dist/css/shepherd.css';
import './Onboarding.css';

import { hasTourBeenShown, markTourAsShown, defaultShowHandler } from './utilities';

const Onboarding = ({
  tours = [],
}: {
  tours?: Array<{
    id: string;
    route: string;
    tourOptions: TourOptions;
    steps: StepOptions[];
  }>;
}) => {
  const Shepherd = useShepherd();
  const location = useLocation();

  /**
   * Show the tour if it hasn't been shown yet based on the current route.
   * Shepherd supplies its own Floating UI middleware — do not inject middleware
   * from the app bundle (production builds can bundle a second @floating-ui/dom
   * copy and throw platform.detectOverflow is not a function).
   */
  useEffect(() => {
    if (!tours.length) {
      return undefined;
    }

    const matchingTour = tours.find(tour => tour.route === location.pathname);
    if (!matchingTour || hasTourBeenShown(matchingTour.id)) {
      return undefined;
    }

    const tourInstance = new Shepherd.Tour({
      ...matchingTour.tourOptions,
      defaultStepOptions: {
        ...matchingTour.tourOptions?.defaultStepOptions,
        when: {
          ...matchingTour.tourOptions?.defaultStepOptions?.when,
          show:
            matchingTour.tourOptions?.defaultStepOptions?.when?.show ||
            (() => defaultShowHandler(Shepherd)),
        },
      },
    });

    matchingTour.steps.forEach(step => tourInstance.addStep(step));

    try {
      tourInstance.start();
      // Preserve prior behavior: mark once when the tour starts so users are not
      // prompted again on the next viewer visit (same as before this fix).
      markTourAsShown(matchingTour.id);
    } catch (error) {
      console.warn('[Onboarding] Tour failed to start; marking as shown to avoid blocking the viewer:', error);
      markTourAsShown(matchingTour.id);
    }

    return () => {
      try {
        if (tourInstance.isActive()) {
          tourInstance.cancel();
        }
      } catch (_) {
        // ignore cleanup errors during route change
      }
    };
  }, [Shepherd, tours, location.pathname]);

  return null;
};

export { Onboarding };
